import { Currency, Formula, formatToDollar } from "@inrev/common";
import { type FC, Fragment, type KeyboardEvent } from "react";
import { Icon } from "../../primitives";
import { parseDraftToNumber } from "../DataGrid";
import {
	useFinancialGridDispatch,
	useFinancialGridState,
} from "./state/FinancialGridStateProvider";
import type { FinancialStatementPeriodId, PeriodViewModel, ViewMode } from "./types";

type PendingLineItemRowProps = {
	periods: PeriodViewModel[];
	viewMode: ViewMode;
	onCommitCreate: (
		label: string,
		valueDrafts: Record<FinancialStatementPeriodId, string>,
		adjustmentDrafts: Record<FinancialStatementPeriodId, string>,
	) => void;
};

// Returns the previewed allowed value, or null (rendered as "--") when the
// adjustment is empty/invalid. An empty given is treated as 0, mirroring commit.
const previewAllowed = (valueDraft: string, adjustmentDraft: string): number | null => {
	const trimmedAdjustment = adjustmentDraft.trim();
	if (trimmedAdjustment === "") return null;
	const parsedGiven = parseDraftToNumber(valueDraft).match(
		(value) => value ?? 0,
		() => null,
	);
	if (parsedGiven === null) return null;
	return Formula.parse(trimmedAdjustment).match(
		(formula) => {
			const delta = Formula.evaluate(parsedGiven, formula);
			return Number.isFinite(delta) ? Currency.add(parsedGiven, Currency.round(delta)) : null;
		},
		() => null,
	);
};

// Validates an adjustment formula: parse must succeed and the result against the
// given draft (empty given treated as 0) must be finite. Mirrors the single-cell
// adjustment commit so a parseable-but-non-finite formula (e.g. `g / 0`) is caught.
const adjustmentError = (valueDraft: string, adjustmentDraft: string): string | null => {
	const parsedGiven = parseDraftToNumber(valueDraft).match(
		(value) => value ?? 0,
		() => 0,
	);
	return Formula.parse(adjustmentDraft).match(
		(formula) => {
			const delta = Formula.evaluate(parsedGiven, formula);
			return Number.isFinite(delta) ? null : "Result is not a finite number";
		},
		(error) => error.message,
	);
};

export const PendingLineItemRow: FC<PendingLineItemRowProps> = ({
	periods,
	viewMode,
	onCommitCreate,
}) => {
	const state = useFinancialGridState();
	const dispatch = useFinancialGridDispatch();

	if (state.edit.kind !== "creating_row") return null;

	const { labelDraft, valueDrafts, valueErrors, adjustmentDrafts, adjustmentErrors, formError } =
		state.edit;

	const validateValue = (periodId: FinancialStatementPeriodId, draft: string) => {
		parseDraftToNumber(draft).match(
			() => dispatch({ type: "clear_create_value_error", periodId }),
			(error) => dispatch({ type: "set_create_value_error", periodId, message: error.message }),
		);
	};

	const validateAdjustment = (periodId: FinancialStatementPeriodId, draft: string) => {
		if (draft.trim() === "") {
			dispatch({ type: "clear_create_adjustment_error", periodId });
			return;
		}
		const error = adjustmentError(valueDrafts[periodId] ?? "", draft);
		if (error === null) {
			dispatch({ type: "clear_create_adjustment_error", periodId });
		} else {
			dispatch({ type: "set_create_adjustment_error", periodId, message: error });
		}
	};

	const commit = () => {
		let hasParseError = false;
		for (const period of periods) {
			parseDraftToNumber(valueDrafts[period.id] ?? "").match(
				() => dispatch({ type: "clear_create_value_error", periodId: period.id }),
				(error) => {
					hasParseError = true;
					dispatch({
						type: "set_create_value_error",
						periodId: period.id,
						message: error.message,
					});
				},
			);

			const adjustmentDraft = adjustmentDrafts[period.id] ?? "";
			if (adjustmentDraft.trim() === "") {
				dispatch({ type: "clear_create_adjustment_error", periodId: period.id });
			} else {
				const error = adjustmentError(valueDrafts[period.id] ?? "", adjustmentDraft);
				if (error === null) {
					dispatch({ type: "clear_create_adjustment_error", periodId: period.id });
				} else {
					hasParseError = true;
					dispatch({ type: "set_create_adjustment_error", periodId: period.id, message: error });
				}
			}
		}
		if (hasParseError) return;
		onCommitCreate(labelDraft, valueDrafts, adjustmentDrafts);
	};
	const cancel = () => dispatch({ type: "cancel_create_row" });

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			commit();
		} else if (e.key === "Escape") {
			e.preventDefault();
			cancel();
		}
	};

	return (
		<>
			<div
				className="sticky left-0 z-10 bg-surface-accent flex items-center pl-8 pr-2 border-r border-bc-default gap-2"
				data-testid="pending-line-item-row"
			>
				<input
					type="text"
					value={labelDraft}
					onChange={(e) => dispatch({ type: "change_create_label", label: e.target.value })}
					onKeyDown={handleKeyDown}
					placeholder="Name line item..."
					autoFocus
					data-testid="pending-line-item-label-input"
					className="flex-1 min-w-0 bg-surface-base border border-bc-strong rounded-sm px-2 py-0.5 text-sm text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
				/>
				<button
					type="button"
					onClick={commit}
					data-testid="pending-line-item-confirm"
					aria-label="Confirm new line item"
					className="inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-surface-light shrink-0"
				>
					<Icon type="check" size="mini" color="confirm" />
				</button>
				<button
					type="button"
					onClick={cancel}
					data-testid="pending-line-item-cancel"
					aria-label="Cancel new line item"
					className="inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-surface-light shrink-0"
				>
					<Icon type="x-mark" size="mini" color="danger" />
				</button>
			</div>
			{periods.map((period) => {
				const valueDraft = valueDrafts[period.id] ?? "";
				const cellError = valueErrors[period.id] ?? formError;
				const adjustmentDraft = adjustmentDrafts[period.id] ?? "";
				const adjustmentError = adjustmentErrors[period.id];
				const allowedPreview = previewAllowed(valueDraft, adjustmentDraft);
				return (
					<Fragment key={period.id}>
						{viewMode !== "allowed_only" && (
							<div className="flex items-center justify-end px-2 bg-surface-accent border-r border-bc-subtle">
								<input
									type="text"
									inputMode="decimal"
									value={valueDraft}
									onChange={(e) =>
										dispatch({
											type: "change_create_value",
											periodId: period.id,
											value: e.target.value,
										})
									}
									onBlur={(e) => validateValue(period.id, e.target.value)}
									onKeyDown={handleKeyDown}
									placeholder="0"
									aria-invalid={cellError !== undefined}
									title={cellError}
									data-testid={`pending-line-item-value-${period.id}`}
									className={`w-full text-right bg-surface-base border rounded-sm px-1 py-0.5 text-sm text-primary tabular-nums focus:outline-none focus:ring-1 ${
										cellError !== undefined
											? "border-danger focus:ring-danger"
											: "border-bc-strong focus:ring-brand-primary"
									}`}
								/>
							</div>
						)}
						{viewMode === "all" && (
							<div className="flex items-center justify-end px-2 bg-surface-accent border-r border-bc-subtle">
								<input
									type="text"
									value={adjustmentDraft}
									onChange={(e) =>
										dispatch({
											type: "change_create_adjustment",
											periodId: period.id,
											value: e.target.value,
										})
									}
									onBlur={(e) => validateAdjustment(period.id, e.target.value)}
									onKeyDown={handleKeyDown}
									placeholder="--"
									aria-invalid={adjustmentError !== undefined}
									title={adjustmentError}
									data-testid={`pending-line-item-adjustment-${period.id}`}
									className={`w-full text-right bg-surface-base border rounded-sm px-1 py-0.5 text-sm text-primary tabular-nums focus:outline-none focus:ring-1 ${
										adjustmentError !== undefined
											? "border-danger focus:ring-danger"
											: "border-bc-strong focus:ring-brand-primary"
									}`}
								/>
							</div>
						)}
						<div className="flex items-center justify-end px-2 bg-surface-accent text-tertiary text-sm tabular-nums border-r border-bc-default">
							{allowedPreview === null
								? "--"
								: (formatToDollar(allowedPreview, {
										accounting: true,
										round: true,
										omitSymbol: true,
									}) ?? "--")}
						</div>
					</Fragment>
				);
			})}
		</>
	);
};
